import { Fragment, useEffect, useState } from "react";


import styled from "styled-components";

import {
  teacherLocalKey,
  teacherStepLocalKey,
  useTeacherCreation,
} from "context/TeacherCreationContext.jsx";
import { useToast } from "hooks/index.js";
import { appRoute } from "pages/app/app.routes";
import { useNavigate } from "react-router-dom";
import { HelperText } from "ui/base/helperText/HelperText.jsx";
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp.jsx";
import { SelectButton } from "ui/base/selectInput/SelectButton.jsx";
import { Tooltip } from "ui/base/tooltip/Tooltip.jsx";
import { numberOnly, safeLocalStorage } from "utils/index.js";

import { cancellationOptions, classLists } from "conts/profileConst.js";
import { useCancellation } from "hooks/policyHooks/useCancellation";
import { useDiscountPrivate } from "hooks/policyHooks/useDiscountPrivate";

import { ButtonPrimary } from "ui/base/Button/ButtonStyled.js";
import { PlusIcon } from "ui/icons/index.js";
import { PercentIcon } from "ui/icons/inputIcons/PercentIcon.jsx";
import { defaultThemeColors, fonts, themeColor } from "ui/theme/index.js";
import { pixelTrackEvent } from "utils/facebookPixel.js";
import { useMediaQuery } from "react-responsive";

const TEXT_DISCOUNT_POLICY =
  "You can offer Users discount on their first booking or if they book multiple classes at the same time.";

const TEXT_CANCELLATION_POLICY =
  "Cancellation policies are based on the start time of the class. Classes canceled by users within 60 minutes of the start of the class are not subject to refund.";

export const Policies = () => {
  const navigate = useNavigate();

  const {
    formData,
    changeFormData: changeTeacherInfo,
    setFormData,
  } = useTeacherCreation();


  const [formDataErrors, setFormDataErrors] = useState({});

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const {
    getAvailableOptions,
    handleAddCancellationPolicy,
    handleRemoveCancellationPolicy,
    handlePolicyChange,
    cancellationErrors,
    validateCancellationPolicy,
  } = useCancellation(formData, setFormData);

  const {
    getAvailableDiscountPrivate,
    handleAddDiscountPrivatePolicy,
    handleRemoveDiscountPrivatePolicy,
    handleDiscountPrivatePolicyChange,
    discountPrivateErrors,
    validatePrivatePolicy,
  } = useDiscountPrivate(formData, setFormData);

  const onSubmit = () => {
    if (formData.profile_pic && formData.profile_pic.uri.length === 0) {
      useToast("error", "Step 1: Upload profile picture");
      return;
    }

    // Validate cancellation policy
    let submitErrors = {
      ...formDataErrors,
      ...validateCancellationPolicy(formData.cancellation_policy),
    };



    // Validate prices and policies for private class
    submitErrors = {
      ...submitErrors,
      ...validatePrivatePolicy(formData.discount_policy_for_private_class),
    };

    const hasErrors = Object.values(submitErrors).some((error) => error !== "");

    if (hasErrors) {
      useToast("error", "Fill all the fields");
      return;
    }

    safeLocalStorage.setItem(teacherLocalKey, formData);
    safeLocalStorage.setItem(teacherStepLocalKey, 3);
    navigate(appRoute.completeProfileFourth.fullPath);
  };

  useEffect(() => {
    pixelTrackEvent("Enter Instructor Sign-up Page 3");
  }, []);

  const canSubmit = Object.values({...cancellationErrors,  ...discountPrivateErrors}).some((error) => error !== "");

  return (
    <Wrapper>
      <div>
        <FormContainer>
          <RowContainer isMobile={isMobile}>
            <PolicyContainer>
              <Title>Cancellation Policy</Title>
              <PurpleText>{"(Optional)"}</PurpleText>
              <HelperText>
                <p>
                  {"Specify refund for clients who cancel class."}
                </p>
              </HelperText>
              <Spacer>
                {formData.cancellation_policy.map((policy, i) => (
                  <Fragment key={policy.policy}>
                    <FlexContainer>
                      <SelectButton
                        options={getAvailableOptions()}
                        value={cancellationOptions.find(
                          (option) => option.value === policy.policy
                        )}
                        onChange={(selectedOption) => {
                          if (selectedOption?.value === "delete") {
                            handleRemoveCancellationPolicy(policy.policy);
                            return;
                          }

                          handlePolicyChange(
                            "policy",
                            policy.policy,
                            selectedOption.value
                          );
                        }}
                        showDelete
                        bgColor="blue15"
                        valueColor="blue"
                        placeholderColor="blue"
                        borderColor="transparent"
                      />
                      <PaymentInputContainer>
                        <InputSignUp
                          type="number"
                          inputmode="numeric"
                          gap="0.5rem"
                          showIconWhenEmpty
                          icon={<PercentIcon />}
                          iconPosition={"right"}
                          id={`percentage-${i}`}
                          centerInput
                          onInput={numberOnly}
                          placeholder="of payment"
                          value={policy.percentage}
                          maxLength={2}
                          setValue={(e) =>
                            handlePolicyChange(
                              "percentage",
                              policy.policy,
                              e ? +e : e
                            )
                          }
                        />
                      </PaymentInputContainer>
                    </FlexContainer>

                    {cancellationErrors?.[policy.policy] ? (
                      <HelperText
                        color="pink"
                        textAlign="right"
                        style={{ paddingRight: "30px" }}
                      >
                        {cancellationErrors?.[policy.policy]}
                      </HelperText>
                    ) : null}
                  </Fragment>
                ))}
              </Spacer>
              {formData.cancellation_policy.length <
                cancellationOptions.length ? (
                <FlexContainer>
                  <IconContainer onClick={handleAddCancellationPolicy}>
                    <PlusIcon size={20} color="#554DF1" />
                  </IconContainer>
                  <HelperText
                    onClick={handleAddCancellationPolicy}
                    color="placeholder"
                    style={{ cursor: "pointer" }}
                  >
                    Add cancellation policy
                  </HelperText>
                  <div style={{ marginLeft: "auto" }}>
                    <Tooltip
                      text={TEXT_CANCELLATION_POLICY}
                      position="top"
                      id="cancellation_policy"
                    />
                  </div>
                </FlexContainer>
              ) : null}
            </PolicyContainer>

            <PolicyContainer>
              <Title>Discount Policy</Title>
              <PurpleText>{"(Optional)"}</PurpleText>
              <>
                <HelperText>
                  {`Specify discount policy terms.`}
                </HelperText>
                <Spacer>
                  {formData.discount_policy_for_private_class.map(
                    (discountPrivate, i) => (
                      <Fragment key={discountPrivate.policy}>
                        <FlexContainer>
                          <SelectButton
                            options={getAvailableDiscountPrivate()}
                            value={classLists.find(
                              (option) =>
                                option.value === discountPrivate.policy
                            )}
                            onChange={(selectedOption) => {
                              if (selectedOption?.value === "delete") {
                                handleRemoveDiscountPrivatePolicy(
                                  discountPrivate.policy
                                );
                                return;
                              }
                              handleDiscountPrivatePolicyChange(
                                "policy",
                                discountPrivate.policy,
                                selectedOption.value
                              );
                            }}
                            showDelete
                            bgColor="blue15"
                            valueColor="blue"
                            placeholderColor="blue"
                            borderColor="transparent"
                          />
                          <PaymentInputContainer>
                            <InputSignUp
                              type="number"
                              inputmode="numeric"
                              icon={<PercentIcon />}
                              iconPosition={"right"}
                              id={`percentage-${i}`}
                              gap="0.5rem"
                              centerInput
                              showIconWhenEmpty
                              onInput={numberOnly}
                              placeholder="of payment"
                              value={discountPrivate.percentage}
                              maxLength={2}
                              setValue={(e) =>
                                handleDiscountPrivatePolicyChange(
                                  "percentage",
                                  discountPrivate.policy,
                                  e ? +e : e
                                )
                              }
                            />
                          </PaymentInputContainer>
                        </FlexContainer>

                        {discountPrivateErrors?.[discountPrivate.policy] ? (
                          <HelperText
                            color="pink"
                            textAlign="right"
                            style={{ paddingRight: "30px" }}
                          >
                            {discountPrivateErrors?.[discountPrivate.policy]}
                          </HelperText>
                        ) : null}
                      </Fragment>
                    )
                  )}
                </Spacer>
                {formData.discount_policy_for_private_class.length <
                  classLists.length ? (
                  <FlexContainer>
                    <IconContainer onClick={handleAddDiscountPrivatePolicy}>
                      <PlusIcon size={20} color="#554DF1" />
                    </IconContainer>
                    <HelperText
                      onClick={handleAddDiscountPrivatePolicy}
                      color="placeholder"
                      style={{ cursor: "pointer" }}
                    >
                      Add discount policy
                    </HelperText>

                    <div style={{ marginLeft: "auto" }}>
                      <Tooltip
                        text={TEXT_DISCOUNT_POLICY}
                        position="top"
                        id="discount_private"
                      />
                    </div>
                  </FlexContainer>
                ) : null}
              </>
            </PolicyContainer>
          </RowContainer>
        </FormContainer>

        <InputContainer>
          <div style={{ width: "100%", display: "flex" }}>
            <ButtonPrimary
                disabled={canSubmit}
              onClick={() => {
                pixelTrackEvent(
                  "Instructor Sign-up Page 3: Clicked on Next step"
                );
                onSubmit();
              }}
            >
              Next Step
            </ButtonPrimary>
          </div>
        </InputContainer>
      </div>
    </Wrapper>
  );
};

const Title = styled.h3`
  font-family: Montserrat;
  font-weight: 600;
  font-size: ${fonts.sizes.h5};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;

  & > div {
    display: grid;
    gap: 1rem;
  }
`;

const FormContainer = styled.div`
  display: grid;
  gap: 1rem;
  grid-column: span 2;
  height: fit-content;
  @media (min-width: 1024px) {
    grid-column: span 1;
  }
`;

const RowInput = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  place-items: start;
  gap: 1rem;
`;

const InputTooltipContainer = styled.div`
  position: relative;
  & > .tooltip-container {
    position: absolute;
    right: -10px;
    top: -1.5px;
    @media (min-width: 640px) {
      right: 20%;
    }
    @media (min-width: 1024px) {
      right: 15%;
      top: 0px;
    }
  }
`;

const InputContainer = styled.div`
  grid-column: span 2;
`;

const EstimateContainer = styled.div`
  display: grid;
  gap: 1rem;
`;
const EstimateInnerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
const Estimate = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  color: ${defaultThemeColors.placeholder};
  height: 62px;
`;

const PolicyContainer = styled.div`
  display: grid;
  gap: 1rem;
  height: min-content;
`;

const RowContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ isMobile }) => isMobile ? "repeat(1, 1fr)" : "repeat(2, 1fr)"};
  gap: 2rem;
`;

const IconContainer = styled.div`
  height: ${({ size }) => size ?? "45px"};
  width: ${({ size }) => size ?? "45px"};
  background-color: ${({ backgroundColor }) =>
    backgroundColor ?? defaultThemeColors.lightBlue};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 99px;
  color: ${({ color }) => color ?? defaultThemeColors.white};
`;
const FlexContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const PaymentInputContainer = styled.div`
  min-width: 142px;
  max-width: 160px;
`;

const PurpleText = styled.p`
  display: inline;
  color: ${themeColor("blue")};
`;

const Spacer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;
